import React, {useContext, useMemo, useRef, useState} from 'react';
import {CitizenContext} from "../context/citizen-context.jsx";
import {Card} from "../../../../../../components/card/card.jsx";
import {v4} from 'uuid';
import {getCities, getNeighborhoods, getStreets} from "../../../../../../utils/location-utils.js";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import optionsUtils from "../../../../../../utils/options-utils.js";
import InputForm from "../../../../../../components/input/input-form.jsx";
import InputSwitch from "../../../../../../components/input/input-switch.jsx";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import {MaskDefault} from "../../../../../../utils/mask.js";
import Button from "../../../../../../components/button/button.jsx";
import {FaPlus} from "react-icons/fa";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import GenericEditRemoveCellRenderer from "../../../../../../components/ag-grid/generic-edit-remove-cell-renderer.jsx";
import InitialAddress from "../resources/initial-address.js";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import {IoMdClose} from "react-icons/io";
import citizenAlertMessages from "../../utils/citizen-alert-messages.js";
import placeService from "../../../../../../services/web/place-service.js";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import ModalReverseGeocoding from "../../../../../../components/modal/modal-reverse-geocoding.jsx";
import readImageFile from "../utils/read-image-file.js";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const CitizenGeneralAddress = ({title, addresses, onChange, height = "h-[300px]", isCitizen = false}) => {
    const {fetchData, setAttaches, setIsLoading} = useContext(CitizenContext);

    const [map, setMap] = useState(false);
    const [address, setAddress] = useState(InitialAddress);
    const [targetAddress, setTargetAddress] = useState(undefined);
    const [open, setOpen] = useState(false);

    const gridRef = useRef(undefined);
    const imgRef = useRef(undefined);

    const removeAddressRef = useRef(undefined);

    removeAddressRef.current = (address) => {
        const updated = addresses.filter((i) => i.id !== address.id);
        setAttaches(prev => prev.filter(i => i.attachId !== address.attachId));
        onChange(updated);
    }

    const handleData = (data) => {
        setAddress(data);
        setTargetAddress(data.id);
    }

    const [columnDefs,] = useState([
        { field: "addressType", headerName: "Tipo de local", flex: 1},
        { field: "description", headerName: "Identificação", flex: 1},
        { field: "address", headerName: "Logradouro", flex: 1},
        { field: "neighborhood", headerName: "Bairro", flex: 1},
        { field: "city", headerName: "Município", flex: 1},
        { field: "number", headerName: "Número", width: 100 },
        { field: "zipCode", headerName: "CEP", width: 130},
        {
            headerName: "",
            field: "",
            width: 100,
            cellRenderer: GenericEditRemoveCellRenderer,
            cellRendererParams: {setData: handleData, setOpen, remove: (address) => removeAddressRef.current(address)}
        },
    ]);

    const defaultColDef = useMemo(() => ({
        comparator: aggridUtils.nullLastComparator
    }), []);

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const addAddress = () => {
        setOpen(true);
        setAddress({id: v4(), ...InitialAddress});
        setTargetAddress(undefined)
    };

    const normalizeCoordinate = (value) => typeof value === "string" ? value.replace(",", ".") : value;

    const verifyAddress = (data) => {
        const validations = [
            { field: "addressType", msg: "missing.addressType" },
            { field: "address", msg: "missing.address" },
            { field: "number", msg: "missing.number" },
            { field: "neighborhood", msg: "missing.neighborhood" },
            { field: "city", msg: "missing.city" },
            { field: "latitude", msg: "missing.latitude" },
            { field: "longitude", msg: "missing.longitude" },
        ];

        for (const { field, msg } of validations) {
            if (!data[field]) {
                projectUtils.swalErrorMessage('', citizenAlertMessages[msg], 'Ok');
                return false;
            }
        }

        const latitude = Number(data.latitude);
        if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
            projectUtils.swalErrorMessage('', citizenAlertMessages['invalid.latitude'], 'Ok');
            return false;
        }

        const longitude = Number(data.longitude);
        if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
            projectUtils.swalErrorMessage('', citizenAlertMessages['invalid.longitude'], 'Ok');
            return false;
        }

        if (isCitizen) {
            if (data.availabilityStartTime && !data.availabilityEndTime) {
                projectUtils.swalErrorMessage('', citizenAlertMessages['missing.availabilityEndTime'], 'Ok');
                return false;
            }

            if (!data.availabilityStartTime && data.availabilityEndTime) {
                projectUtils.swalErrorMessage('', citizenAlertMessages['missing.availabilityStartTime'], 'Ok');
                return false;
            }

            if ((data.availabilityStartTime && data.availabilityEndTime) && (data.availabilityStartTime >= data.availabilityEndTime)) {
                projectUtils.swalErrorMessage('', citizenAlertMessages['invalid.scheduleWindow'], 'Ok');
                return false;
            }
        }

        return true;
    }

    const upsertAddress = (newAddress) => {
        const normalized = {
            ...address,
            latitude: normalizeCoordinate(address.latitude),
            longitude: normalizeCoordinate(address.longitude),
        };

        if (!verifyAddress(normalized)) return;

        const updated = newAddress ? [...addresses, normalized] : addresses.map((i) => i.id === targetAddress ? normalized : i);
        const oldAddress = addresses.find((i) => i.id === targetAddress);

        if ((oldAddress?.attachId !== normalized.attachId) || normalized.file) {
            setAttaches(prev => {
                if (normalized.file) {
                    normalized.file.new = true
                    prev.push(normalized.file);
                }
                return prev.filter(i => i.attachId !== oldAddress?.attachId)
            });
        }

        onChange(updated);
        setOpen(false);
    }

    const updateLatLng = (val) => {
        const city = (val.city && val.district) ? `${val.city} - ${val.district}` : null;

        setAddress(prev => ({
            ...prev,
            latitude: val.latitude,
            longitude: val.longitude,
            placeId: null,
            ...(val.changeAddress && {
                description: null,
                city,
                address: val.street,
                number: val.number,
                neighborhood: val.neighborhood,
            })
        }));
    };

    const openMap = () => setMap(true);

    const handleInput = ({target: {id, value}}) => setAddress(prev => ({...prev, [id]: value, placeId: null}));

    const handleArrayItems = (value, target) => setAddress(prev => ({...prev, [target]: value, placeId: null}));

    const handlePlace = (value) => {
        if (!value?.value) {
            setAddress(InitialAddress)
            return
        }

        placeService.getById(value.value)
            .then((data) => {
                setAddress(prev => ({
                    ...prev,
                    placeId: data.id,
                    city: data.city ? `${data.city} - ${data.state}` : null,
                    neighborhood: data.neighborhood || null,
                    number: data.number || "",
                    address: data.street || null,
                    addressType: data.type || null,
                    zipCode: data.zipcode || "",
                    complement: data.complement || "",
                    description: data.name || "",
                    latitude: data.latitude || "",
                    longitude: data.longitude || "",
                    attachId: data.attach_id || null,
                    file: undefined,
                    imgScr: undefined
                }))
            })
    }

    const attachAddressImage = ({target: {files}}) => {
        readImageFile(files, (file, imgScr) => {
            setAddress(prev => ({...prev, file, imgScr, attachId: file.attachId, placeId: null}));
        });
    }

    const removeFile = () => {
        setAddress(prev => ({...prev, file: undefined, imgScr: undefined, attachId: null, placeId: null}));
    }

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>{title}</Card.Title>

                    <Button onClick={addAddress}>
                        <FaPlus />
                    </Button>
                </Card.Header>
                <Card.Body className={"space-y-4"}>
                    <div className={"ag-theme-quartz grid " + height}>
                        <AgGridReact
                            ref={gridRef}
                            rowHeight={35}
                            rowData={addresses}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            paginationAutoPageSize={true}
                            pagination={true}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={theme}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal open={open} setOpen={setOpen} title={"Endereço"} isCloseClick={false} overflow={true}>
                <Modal.Body className={"grid grid-cols-12 gap-4"}>
                    <section className={"col-span-3"}>
                        <div className={"h-[137px]"}>
                            <div
                                className={"cursor-pointer bg-no-repeat bg-contain h-full border border-[#e0e0e0] p-1 rounded"}
                                style={{backgroundImage: (!address.imgScr && address.attachId) ? `url(${API_KEEPER}/ged/attach/${address.attachId})` : address.imgScr ? `url(${address.imgScr})` : `url(${getAssetPath("img/no-img.png")})`, backgroundPosition: "50% center"}}
                            >
                                {
                                    address?.attachId &&
                                    <Button className={"remove-image float-right"} target={"danger"} title={"Remover imagem"} onClick={removeFile}>
                                        <IoMdClose className={"remove-image"}/>
                                    </Button>
                                }
                            </div>
                        </div>
                        <input type="file" accept="image/png, image/jpeg" className="hidden" name="image" onChange={attachAddressImage} ref={imgRef}/>
                        <Button className={"w-full"} target={"primary"} onClick={() => imgRef.current?.click()}>
                            Selecionar foto
                        </Button>
                    </section>
                    <SentryRow className={"col-span-9"}>
                        {
                            isCitizen &&
                            <div className={"col-span-12"}>
                                <SelectComponent
                                    classNameWrapper={"w-1/2"}
                                    value={address.placeId}
                                    options={fetchData?.places ?? []}
                                    label={"Local pré-cadastrado"}
                                    onChange={handlePlace}
                                />
                            </div>
                        }
                        <SelectComponent
                            classNameWrapper={"col-span-4"}
                            value={address.addressType}
                            options={fetchData.locationTypes}
                            label={"Tipo de local *"}
                            onChange={(selected) => handleArrayItems(selected?.value, "addressType")}
                        />

                        <SelectComponent
                            classNameWrapper={"col-span-5"}
                            loadOptions={getStreets}
                            value={optionsUtils.getValue(address.address)}
                            isAsync={true}
                            label="Logradouro *"
                            onChange={(selected) => handleArrayItems(selected?.value, "address")}
                            cacheOptions
                            defaultOptions={[]}
                        />

                        <InputForm
                            id={"number"}
                            label={"Número *"}
                            classNameWrapper={"col-span-3"}
                            value={address.number}
                            onChange={handleInput}
                        />

                        <SelectComponent
                            classNameWrapper={"col-span-4"}
                            loadOptions={getNeighborhoods}
                            value={optionsUtils.getValue(address.neighborhood)}
                            isAsync={true}
                            label="Bairo *"
                            onChange={(selected) => handleArrayItems(selected?.value, "neighborhood")}
                            cacheOptions
                            defaultOptions={[]}
                        />

                        <InputForm
                            id={"complement"}
                            label={"Complemento"}
                            classNameWrapper={"col-span-3"}
                            value={address.complement}
                            onChange={handleInput}
                        />

                        <SelectComponent
                            classNameWrapper={"col-span-5"}
                            loadOptions={getCities}
                            value={optionsUtils.getValue(address.city)}
                            isAsync={true}
                            label="Município *"
                            onChange={(selected) => handleArrayItems(selected?.value, "city")}
                            cacheOptions
                            defaultOptions={[]}
                        />

                        <InputForm
                            id={"zipCode"}
                            label={"CEP"}
                            classNameWrapper={"col-span-3"}
                            value={address.zipCode}
                            onChange={handleInput}
                            mask={MaskDefault.cep}
                        />

                        <InputForm
                            id={"description"}
                            label={"Identificação"}
                            classNameWrapper={"col-span-9"}
                            value={address.description}
                            onChange={handleInput}
                        />
                    </SentryRow>

                    <SentryRow className={"col-span-12"}>
                        <InputForm
                            id={"latitude"}
                            label={"Latitude *"}
                            classNameWrapper={"col-span-3 col-start-4"}
                            value={address.latitude}
                            onChange={handleInput}
                        />

                        <InputForm
                            id={"longitude"}
                            label={"Longitude *"}
                            classNameWrapper={"col-span-3"}
                            value={address.longitude}
                            onChange={handleInput}
                        />

                        <Button target={"primary"} className={"col-span-3"} onClick={() => openMap()}>
                            Mapa
                        </Button>
                    </SentryRow>

                    {
                        isCitizen &&
                        <>
                            <div className={"col-span-12 flex items-center gap-3 my-1"}>
                                <span className={"h-px flex-1 bg-gray-300"}/>
                                <span className={"font-medium "}>Agendamento</span>
                                <span className={"h-px flex-1 bg-gray-300"}/>
                            </div>

                            <SentryRow className={"col-span-12 items-center"}>
                                <InputSwitch
                                    className={"col-span-2"}
                                    id={"visitable"}
                                    label={"Visita"}
                                    checked={!!address.visitable}
                                    onChange={(checked) => setAddress(prev => ({...prev, visitable: checked}))}
                                />

                                <InputSwitch
                                    className={"col-span-2"}
                                    id={"patrollable"}
                                    label={"Ronda"}
                                    checked={!!address.patrollable}
                                    onChange={(checked) => setAddress(prev => ({...prev, patrollable: checked}))}
                                />

                                <InputDatetime
                                    classNameWrapper={"col-span-3"}
                                    id={"availabilityStartTime"}
                                    label={"Horário inicial"}
                                    dateFormat={"LT"}
                                    returnDateObj={false}
                                    selected={address.availabilityStartTime}
                                    onChange={(value) => setAddress(prev => ({...prev, availabilityStartTime: value ?? ""}))}
                                />

                                <InputDatetime
                                    classNameWrapper={"col-span-3"}
                                    id={"availabilityEndTime"}
                                    label={"Horário final"}
                                    dateFormat={"LT"}
                                    returnDateObj={false}
                                    selected={address.availabilityEndTime}
                                    onChange={(value) => setAddress(prev => ({...prev, availabilityEndTime: value ?? ""}))}
                                />
                            </SentryRow>
                        </>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button target={"primary"} onClick={() => upsertAddress(!targetAddress)}>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>

            <ModalReverseGeocoding
                open={map}
                setOpen={setMap}
                address={address}
                setIsLoading={setIsLoading}
                onConfirm={(value) => updateLatLng(value)}
            />
        </>
    );
};

export default CitizenGeneralAddress;